import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient as api } from "@/lib/api-client";
import { Card, CardContent, CardHeader } from "@/modules/shared/ui/components/card";
import { Skeleton } from "@/modules/shared/ui/components/skeleton";

interface LegalDocument {
  version: string;
  content: string;
}

export default function LegalDocumentPage() {
  const { type } = useParams<{ type: string }>();
  const [doc, setDoc] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchDoc() {
      try {
        setLoading(true);
        const response = await api.get(`/legal-documents/${type}`);
        setDoc(response.data);
      } catch (err) {
        console.error("Failed to load document", err);
        setError("Document not found or an error occurred.");
      } finally {
        setLoading(false);
      }
    }
    fetchDoc();
  }, [type]);

  return (
    <div className="container mx-auto py-10 px-4 md:px-0">
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="flex flex-col items-center pb-2">
          <img 
            src="/images/tempered-talent-logo.png" 
            alt="Tempered Talent Logo" 
            className="h-16 w-auto mb-4" 
          />
          {!loading && doc && (
            <p className="text-sm text-muted-foreground mt-2">
              Version: <span className="font-medium">{doc.version}</span>
            </p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[90%]" />
              <Skeleton className="h-4 w-[80%]" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[85%]" />
            </div>
          ) : error ? (
            <div className="text-red-500 font-medium py-10 text-center">
              {error}
            </div>
          ) : doc ? (
            <div
              className="prose prose-sm sm:prose-base lg:prose-lg xl:prose-xl max-w-none prose-headings:text-card-foreground prose-p:text-card-foreground/90 prose-li:text-card-foreground/90 prose-strong:text-card-foreground prose-headings:font-bold prose-a:text-blue-600"
              dangerouslySetInnerHTML={{ __html: doc.content }}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
